<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IStatsSensor } from '@/interfaces/entities/sensor'

const { t } = useI18n()

const props = defineProps<{
    selectedRow: IStatsSensor
    type: 'attention' | 'critical'
}>()

const formatValue = (value: any): string => {
    const num = Number(value)
    return !isNaN(num) && isFinite(num) ? num.toFixed(2) : '-'
}

const baseMedian = computed(() => Number(props.selectedRow.base_median) || 0)
const weekMedian = computed(() => Number(props.selectedRow.week_median) || 0)
const baseIqr = computed(() => Number(props.selectedRow.base_iqr) || 0)
const weekIqr = computed(() => Number(props.selectedRow.week_iqr) || 0)

const medianDifference = computed(() => {
    const diff = weekMedian.value - baseMedian.value
    const percentage = baseMedian.value !== 0 
        ? (diff / baseMedian.value) * 100 
        : 0
    return { diff, percentage }
})

const iqrDifference = computed(() => {
    const diff = weekIqr.value - baseIqr.value
    const percentage = baseIqr.value !== 0 
        ? (diff / baseIqr.value) * 100 
        : 0
    return { diff, percentage }
})

const weekColor = computed(() => props.type === 'critical' ? 'text-red-600' : 'text-orange-600')
const borderColor = 'border-slate-300'
</script>

<template>
    <div class="flex flex-col gap-4 h-full">
        <!-- Card Mediana -->
        <div class="bg-white border shadow-sm p-2 flex-1" :class="borderColor">
            <h3 class="text-sm font-bold text-gray-600 uppercase mb-3">
                {{ t('anomaly_detector.comparison_cards.median_comparison') }}
            </h3>
            <div class="grid grid-cols-2 gap-2 mb-3">
                <div>
                    <p class="text-xs text-gray-500 mb-1">{{ t('anomaly_detector.comparison_cards.base_period') }}</p>
                    <p class="text-2xl font-bold text-slate-600">
                        {{ formatValue(baseMedian) }}
                    </p>
                </div>
                <div>
                    <p class="text-xs text-gray-500 mb-1">{{ t('anomaly_detector.comparison_cards.week') }}</p>
                    <p class="text-2xl font-bold" :class="weekColor">
                        {{ formatValue(weekMedian) }}
                    </p>
                </div>
            </div>
            <div class="pt-2 border-t border-gray-200">
                <p class="text-xs text-gray-500">{{ t('anomaly_detector.comparison_cards.variation') }}</p>
                <p class="text-sm font-semibold" :class="medianDifference.diff >= 0 ? 'text-green-600' : 'text-red-600'">
                    {{ medianDifference.diff >= 0 ? '+' : '' }}{{ formatValue(medianDifference.diff) }}
                    ({{ medianDifference.percentage >= 0 ? '+' : '' }}{{ formatValue(medianDifference.percentage) }}%)
                </p>
            </div>
        </div>

        <!-- Card IQR -->
        <div class="bg-white border shadow-sm p-4 flex-1" :class="borderColor">
            <h3 class="text-sm font-bold text-gray-600 uppercase mb-3">
                {{ t('anomaly_detector.comparison_cards.iqr_comparison') }}
            </h3>
            <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                    <p class="text-xs text-gray-500 mb-1">{{ t('anomaly_detector.comparison_cards.base_period') }}</p>
                    <p class="text-2xl font-bold text-slate-600">
                        {{ formatValue(baseIqr) }}
                    </p>
                </div>
                <div>
                    <p class="text-xs text-gray-500 mb-1">{{ t('anomaly_detector.comparison_cards.week') }}</p>
                    <p class="text-2xl font-bold" :class="weekColor">
                        {{ formatValue(weekIqr) }}
                    </p>
                </div>
            </div>
            <div class="pt-2 border-t border-gray-200">
                <p class="text-xs text-gray-500">{{ t('anomaly_detector.comparison_cards.variation') }}</p>
                <p class="text-sm font-semibold" :class="iqrDifference.diff >= 0 ? 'text-green-600' : 'text-red-600'">
                    {{ iqrDifference.diff >= 0 ? '+' : '' }}{{ formatValue(iqrDifference.diff) }}
                    ({{ iqrDifference.percentage >= 0 ? '+' : '' }}{{ formatValue(iqrDifference.percentage) }}%)
                </p>
            </div>
        </div>
    </div>
</template>
